// src/utils/submissionValidation.ts
// ──────────────────────────────────────────────────────────────
// Pre-submission validation layer for prior auth cases.
// Runs client-side checks against case data and surfaces
// warnings BEFORE the physician submits.
//
// This is NOT clinical decision-making. All checks are:
//   1. Completeness checks (missing fields)
//   2. Documentation sufficiency checks
//   3. Insurer requirement checks (publicly available info)
//   4. Coding specificity observations (clinically-driven)
// ──────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────

export type CheckSeverity = "error" | "warning" | "info" | "pass";

export type CheckCategory =
  | "completeness"
  | "documentation"
  | "insurer"
  | "coding";

export interface ValidationCheck {
  id: string;
  category: CheckCategory;
  severity: CheckSeverity;
  title: string;
  detail: string;
  /** Informational disclaimer for coding-related checks */
  disclaimer?: string;
}

export interface ValidationResult {
  checks: ValidationCheck[];
  score: number; // 0-100 readiness score
  readyToSubmit: boolean;
}

export interface CaseDataForValidation {
  diagnoses?: string;
  request_details?: string;
  insurance_company?: string;
  plan_type?: string;
  tried_already?: string;
  additional_context?: string;
  request_type?: string;
  case_status?: string;
}

// ── Insurer Normalization ─────────────────────────────────────

export function normalizeInsurer(raw?: string): string | undefined {
  if (!raw) return undefined;
  const lower = raw.toLowerCase().trim();
  const map: Record<string, string> = {
    "united": "UnitedHealthcare",
    "unitedhealthcare": "UnitedHealthcare",
    "uhc": "UnitedHealthcare",
    "aetna": "Aetna",
    "cigna": "Cigna",
    "anthem": "Anthem Blue Cross Blue Shield",
    "bcbs": "Blue Cross Blue Shield",
    "blue cross": "Blue Cross Blue Shield",
    "bluecross": "Blue Cross Blue Shield",
    "humana": "Humana",
    "kaiser": "Kaiser Permanente",
    "molina": "Molina Healthcare",
    "centene": "Centene",
    "wellcare": "WellCare",
    "tricare": "TRICARE",
    "medicare": "Medicare",
    "medicaid": "Medicaid",
  };
  if (map[lower]) return map[lower];
  for (const [key, canonical] of Object.entries(map)) {
    if (lower.startsWith(key)) return canonical;
  }
  return raw.trim().replace(/\b\w/g, c => c.toUpperCase());
}

// ── ICD-10 Specificity ───────────────────────────────────────
// Unspecified codes that commonly trigger denials because they
// lack the clinical specificity reviewers expect.
// This is publicly available coding guidance, not clinical advice.

const UNSPECIFIED_CODES: Record<string, { description: string; note: string }> = {
  "M54.5":  { description: "Low back pain, unspecified", note: "Your clinical notes may support a more specific code reflecting the documented presentation (e.g., radiculopathy, sciatica, disc disorder)" },
  "M54.50": { description: "Low back pain, unspecified", note: "Your clinical notes may support a more specific code reflecting the documented presentation" },
  "M79.3":  { description: "Panniculitis, unspecified", note: "Clinical documentation may support a more specific soft tissue diagnosis" },
  "R10.9":  { description: "Unspecified abdominal pain", note: "Your clinical notes may support a more specific abdominal pain code based on location and character" },
  "J06.9":  { description: "Acute upper respiratory infection, unspecified", note: "Clinical documentation may support a site-specific diagnosis" },
  "R51":    { description: "Headache", note: "Your clinical notes may support a more specific headache classification based on documented characteristics" },
  "R51.9":  { description: "Headache, unspecified", note: "Your clinical notes may support a more specific headache classification" },
  "M25.50": { description: "Pain in unspecified joint", note: "Clinical documentation likely specifies which joint — a lateralized, site-specific code may more accurately reflect your notes" },
  "G89.29": { description: "Other chronic pain", note: "Your clinical documentation may support a more specific pain diagnosis based on etiology and location" },
  "M54.9":  { description: "Dorsalgia, unspecified", note: "Your notes likely describe a more specific spinal region — a site-specific code may better reflect the documented presentation" },
  "R07.9":  { description: "Chest pain, unspecified", note: "Clinical documentation may support a more specific chest pain classification" },
  "K21.0":  { description: "GERD with esophagitis", note: "Consider whether documentation supports a more specific esophagitis classification" },
  "N39.0":  { description: "Urinary tract infection, site not specified", note: "Clinical documentation may specify the anatomic site of infection" },
  "J44.1":  { description: "COPD with acute exacerbation", note: "Documentation may support additional specificity regarding the exacerbation type" },
  "F41.9":  { description: "Anxiety disorder, unspecified", note: "Clinical assessment may support a more specific anxiety diagnosis based on documented symptoms" },
  "F32.9":  { description: "Major depressive disorder, single episode, unspecified", note: "Clinical assessment may support severity specification based on documented symptoms" },
};

// ── Conservative Treatment Keywords ──────────────────────────

const CONSERVATIVE_KEYWORDS = [
  "physical therapy", "pt", "physiotherapy",
  "nsaid", "ibuprofen", "naproxen", "meloxicam", "anti-inflammatory",
  "acetaminophen", "tylenol",
  "rest", "ice", "heat", "activity modification",
  "stretching", "exercise", "home exercise",
  "injection", "steroid", "cortisone",
  "chiropractic", "manipulation",
  "massage", "acupuncture",
  "brace", "splint", "orthotics",
  "weight loss", "lifestyle modification",
  "medication", "oral medication",
  "cognitive behavioral", "cbt",
];

// ── Insurer-Specific Requirements ────────────────────────────
// Publicly available submission requirements.

interface InsurerRequirement {
  id: string;
  title: string;
  detail: string;
  check: (caseData: CaseDataForValidation) => boolean;
}

const INSURER_REQUIREMENTS: Record<string, InsurerRequirement[]> = {
  "UnitedHealthcare": [
    {
      id: "uhc_conservative",
      title: "Conservative treatment documentation",
      detail: "UnitedHealthcare typically requires documented conservative treatment trial (4-6 weeks) for advanced imaging and procedures",
      check: (c) => !hasConservativeTreatment(c),
    },
  ],
  "Aetna": [
    {
      id: "aetna_conservative",
      title: "Conservative treatment documentation",
      detail: "Aetna clinical policy bulletins typically require documentation of conservative treatment attempts",
      check: (c) => !hasConservativeTreatment(c),
    },
  ],
  "Cigna": [
    {
      id: "cigna_conservative",
      title: "Conservative treatment documentation",
      detail: "Cigna coverage policies typically require documentation of conservative treatment before approving advanced services",
      check: (c) => !hasConservativeTreatment(c),
    },
  ],
  "Anthem Blue Cross Blue Shield": [
    {
      id: "anthem_conservative",
      title: "Conservative treatment documentation",
      detail: "Anthem medical policies typically require prior conservative treatment for advanced imaging and procedures",
      check: (c) => !hasConservativeTreatment(c),
    },
  ],
};

// ── Helpers ──────────────────────────────────────────────────

function parseICDCodes(raw: string): string[] {
  const parts = raw.split(/[;,]/).map(s => s.trim()).filter(Boolean);
  const codes: string[] = [];
  for (const part of parts) {
    const match = part.match(/^([A-Z]\d{2}\.?\d{0,4})/i);
    if (match) codes.push(match[1].toUpperCase());
  }
  return codes;
}

function hasConservativeTreatment(caseData: CaseDataForValidation): boolean {
  const combined = [
    caseData.tried_already || "",
    caseData.additional_context || "",
    caseData.request_details || "",
  ].join(" ").toLowerCase();

  return CONSERVATIVE_KEYWORDS.some(kw => combined.includes(kw));
}

function hasDurationMentioned(caseData: CaseDataForValidation): boolean {
  const combined = [
    caseData.tried_already || "",
    caseData.additional_context || "",
  ].join(" ").toLowerCase();

  // Look for patterns like "6 weeks", "3 months", "8 wks", etc.
  return /\d+\s*(weeks?|wks?|months?|mos?|days?)\b/.test(combined);
}

// ── Main Validation Function ─────────────────────────────────

export function validateSubmission(
  caseData: CaseDataForValidation,
  normalizedInsurer?: string
): ValidationResult {
  const checks: ValidationCheck[] = [];

  // ── 1. Completeness Checks ───────────────────────────────

  if (!caseData.insurance_company?.trim()) {
    checks.push({
      id: "missing_insurer",
      category: "completeness",
      severity: "error",
      title: "Insurance company required",
      detail: "Specify the insurance company to enable insurer-specific guidance and letter generation",
    });
  } else {
    checks.push({
      id: "has_insurer",
      category: "completeness",
      severity: "pass",
      title: "Insurance company specified",
      detail: normalizedInsurer || caseData.insurance_company,
    });
  }

  if (!caseData.diagnoses?.trim()) {
    checks.push({
      id: "missing_diagnosis",
      category: "completeness",
      severity: "warning",
      title: "No diagnosis codes entered",
      detail: "Adding ICD-10 codes strengthens your submission — algorithmic review systems check diagnosis-to-procedure pairing",
    });
  } else {
    checks.push({
      id: "has_diagnosis",
      category: "completeness",
      severity: "pass",
      title: "Diagnosis codes provided",
      detail: caseData.diagnoses,
    });
  }

  if (!caseData.request_details?.trim()) {
    checks.push({
      id: "missing_request",
      category: "completeness",
      severity: "error",
      title: "Service/procedure details required",
      detail: "Describe the service being requested for authorization",
    });
  } else {
    checks.push({
      id: "has_request",
      category: "completeness",
      severity: "pass",
      title: "Service details provided",
      detail: caseData.request_details,
    });
  }

  // ── 2. Documentation Sufficiency ─────────────────────────

  if (hasConservativeTreatment(caseData)) {
    checks.push({
      id: "has_conservative",
      category: "documentation",
      severity: "pass",
      title: "Conservative treatment documented",
      detail: "Prior treatment attempts are referenced in your case details",
    });

    // Check for duration
    if (!hasDurationMentioned(caseData)) {
      checks.push({
        id: "no_duration",
        category: "documentation",
        severity: "warning",
        title: "Treatment duration not specified",
        detail: "Documenting the duration of conservative treatment (e.g., \"6 weeks of physical therapy\") strengthens submissions — many insurer policies reference specific timeframes",
      });
    } else {
      checks.push({
        id: "has_duration",
        category: "documentation",
        severity: "pass",
        title: "Treatment duration documented",
        detail: "Timeframe for conservative treatment is referenced",
      });
    }
  } else {
    checks.push({
      id: "no_conservative",
      category: "documentation",
      severity: "warning",
      title: "No conservative treatment documented",
      detail: "Most insurers require documentation of prior conservative treatment before approving advanced imaging or procedures. If applicable, add details about treatments already attempted.",
    });
  }

  if (!caseData.additional_context?.trim() && !caseData.tried_already?.trim()) {
    checks.push({
      id: "no_clinical_notes",
      category: "documentation",
      severity: "info",
      title: "No additional clinical notes",
      detail: "Adding clinical context (symptoms, exam findings, functional limitations) gives the AI more material to build a stronger medical necessity argument",
    });
  }

  // ── 3. ICD-10 Specificity ────────────────────────────────

  if (caseData.diagnoses?.trim()) {
    const codes = parseICDCodes(caseData.diagnoses);

    for (const code of codes) {
      const unspecified = UNSPECIFIED_CODES[code];
      if (unspecified) {
        checks.push({
          id: `unspecified_${code}`,
          category: "coding",
          severity: "warning",
          title: `${code} — ${unspecified.description}`,
          detail: unspecified.note,
          disclaimer: "Coding decisions are the responsibility of the treating physician based on clinical documentation",
        });
      }
    }

    if (codes.length > 0 && !codes.some(c => UNSPECIFIED_CODES[c])) {
      checks.push({
        id: "specific_codes",
        category: "coding",
        severity: "pass",
        title: "Diagnosis codes appear specific",
        detail: `${codes.length} code${codes.length > 1 ? "s" : ""} entered`,
      });
    }
  }

  // ── 4. Insurer-Specific Requirements ─────────────────────

  const insurerKey = normalizedInsurer || caseData.insurance_company?.trim();
  if (insurerKey) {
    const requirements = INSURER_REQUIREMENTS[insurerKey];
    if (requirements) {
      for (const req of requirements) {
        if (req.check(caseData)) {
          checks.push({
            id: req.id,
            category: "insurer",
            severity: "warning",
            title: req.title,
            detail: req.detail,
          });
        }
      }
    }
  }

  // ── Compute Readiness Score ──────────────────────────────

  const errors = checks.filter(c => c.severity === "error").length;
  const warnings = checks.filter(c => c.severity === "warning").length;
  const passes = checks.filter(c => c.severity === "pass").length;
  const total = errors + warnings + passes;

  let score = total > 0 ? Math.round((passes / total) * 100) : 0;
  if (errors > 0) score = Math.min(score, 40);
  if (warnings > 0 && errors === 0) score = Math.min(score, 85);
  if (errors === 0 && warnings === 0) score = 100;

  return {
    checks,
    score,
    readyToSubmit: errors === 0,
  };
}